import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'

import './index.css'
import { useAuthStore } from '@/core/store/authStore'
import { ErrorBoundary } from '@/core/components/ErrorBoundary'

import { router } from './router'

// After a new deployment, a running SPA may try to lazy-load chunks whose
// hashed filenames no longer exist. Detect those failures and reload once so
// the browser fetches the fresh index.html with the new chunk manifest.
const CHUNK_ERROR_MARKERS = [
  'Failed to fetch dynamically imported module',
  'Failed to load module script',
  'Loading chunk',
  'Cannot find module',
]

function isChunkLoadError(message = '') {
  return CHUNK_ERROR_MARKERS.some((marker) => message.includes(marker))
}

function reloadIfStaleChunk() {
  const lastReload = sessionStorage.getItem('chunk_reload_ts')
  const now = Date.now()
  if (!lastReload || now - Number(lastReload) > 60000) {
    sessionStorage.setItem('chunk_reload_ts', String(now))
    window.location.reload()
    return true
  }
  return false
}

window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.message) && reloadIfStaleChunk()) {
    event.preventDefault()
  }
})

window.addEventListener('unhandledrejection', (event) => {
  const message =
    typeof event.reason === 'string'
      ? event.reason
      : event.reason?.message || String(event.reason)
  if (isChunkLoadError(message) && reloadIfStaleChunk()) {
    event.preventDefault()
  }
})

// Vite-specific event fired when a preloaded/dynamic chunk fails to load.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadIfStaleChunk()) {
    event.preventDefault()
  }
})

// Kick off auth check before first render so the router has the latest state
useAuthStore.getState().checkAuth()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary onChunkLoadError={reloadIfStaleChunk}>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </StrictMode>,
)
